'use client';

import { type FC, forwardRef, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import type { Wallet } from '@/api/auth';
import { Button } from '@/components/buttons';
import { CurrencyItem } from '@/components/Deposit/components/CurrencyBlocks/CurrencyItem';
import { ModalSelect, ModalSelectContainer } from '@/components/ModalSelect';
import { ScrollBlock } from '@/components/ScrollBlock';
import { amountWithCurrencySymbol, mapCurrencyName } from '@/helpers/currency';
import type { ThemeMode } from '@/helpers/enums';
import { useGetCommonStore } from '@/store';
import { EditCurrenciesModal } from '../Deposit/components/CurrencyBlocks/EditCurrenciesModal';
import { IconComponent } from '../IconComponent';
import { ScOptionsWalletsList, type ScSelectDepositProps } from './styled';

type SelectDepositContentProps = Pick<
  ScSelectDepositProps,
  'isInputAddon' | 'mode'
> & {
  noEditCurrencyBtn?: boolean;
  onOpen: () => void;
  onChange: (val: Wallet['currency']) => void;
};

export const SelectDepositContent = forwardRef<
  HTMLDivElement,
  SelectDepositContentProps
>(({ isInputAddon, noEditCurrencyBtn, mode, onOpen, onChange }, ref) => {
  const { t } = useTranslation('deposits');
  const [isEditCurrenciesOpen, setIsEditCurrenciesOpen] = useState(false);
  const useCommonStore = useGetCommonStore();

  const { extendedUserWallets, isMobileDevice } = useCommonStore(
    useShallow((state) => ({
      extendedUserWallets: state.extendedUserWallets,
      isMobileDevice: state.isMobileDevice,
    }))
  );

  const optionsWallets = useMemo(
    () =>
      extendedUserWallets.map(
        ({ amountCents, currency, subCurrency, icon }) => {
          const name = mapCurrencyName[currency];

          return {
            name: `${amountWithCurrencySymbol(subCurrency, amountCents)}${
              name ? ` | ${name}` : ''
            }`,
            value: currency,
            icon,
          };
        }
      ),
    [extendedUserWallets]
  );

  return (
    <>
      {isMobileDevice ? (
        isInputAddon ? (
          <ModalSelectContainer
            icon={<IconComponent name="currencyIcon" size="40px" />}
            innerAlignBottom
            onClose={onOpen}
            openOptionsInPortal
            title={t('editCurrency.myCurrencies')}
          >
            <ScOptionsWalletsList>
              <div className="list">
                {optionsWallets.map(({ value, name, icon }) => (
                  <CurrencyItem
                    currencyCode={value}
                    icon={icon}
                    isActive
                    key={value}
                    label={name}
                    noAction
                  />
                ))}
              </div>
              {!noEditCurrencyBtn && (
                <EditCurrenciesButton
                  large
                  mode={mode}
                  onClick={() => setIsEditCurrenciesOpen(true)}
                />
              )}
            </ScOptionsWalletsList>
          </ModalSelectContainer>
        ) : (
          <ModalSelect
            cols={1}
            icon={<IconComponent name="currencyIcon" size="40px" />}
            innerAlignBottom
            onChange={(value) => {
              onChange(value as Wallet['currency']);
            }}
            onClose={onOpen}
            openOptionsInPortal
            options={extendedUserWallets.map(
              ({ currency, amountCents, subCurrency }) => ({
                name: `${amountCents} ${subCurrency}`,
                value: currency,
              })
            )}
            title={t('currency')}
          />
        )
      ) : (
        <DepositOptions
          deposits={extendedUserWallets}
          handleChange={onChange}
          isInputAddon={isInputAddon}
          mode={mode}
          noEditCurrencyBtn={noEditCurrencyBtn}
          onClick={() => setIsEditCurrenciesOpen(true)}
          ref={ref}
        />
      )}
      {isEditCurrenciesOpen && (
        <EditCurrenciesModal onClose={() => setIsEditCurrenciesOpen(false)} />
      )}
    </>
  );
});

type WalletOptions = Pick<Wallet, 'amountCents' | 'currency'> & {
  subCurrency: string;
};

interface DepositOptionsProps {
  deposits: WalletOptions[];
  handleChange: (currency: Wallet['currency']) => void;
  isInputAddon?: boolean;
  mode?: ThemeMode;
  noEditCurrencyBtn?: boolean;
  onClick: () => void;
}

const DepositOptions = forwardRef<HTMLDivElement, DepositOptionsProps>(
  (
    { deposits, handleChange, isInputAddon, mode, noEditCurrencyBtn, onClick },
    ref
  ) => {
    const useCommonStore = useGetCommonStore();

    const user = useCommonStore.use.user();

    const maxBlockHeight = useMemo(() => {
      const totalHeight = deposits.length * 40 - 16;
      const max = isInputAddon ? 144 : 180;

      return `${totalHeight > max ? max : totalHeight}px`;
    }, [deposits.length, isInputAddon]);

    return (
      <div className="options" ref={ref}>
        <ScrollBlock height={maxBlockHeight} mode={mode} scrollTopMargin={0}>
          <ul>
            {deposits.map(({ currency, amountCents, subCurrency }) => (
              <li
                className={
                  currency === user?.currency || deposits.length === 1
                    ? 'active'
                    : ''
                }
                key={currency}
                onClick={() => handleChange(currency)}
              >
                <>
                  {amountWithCurrencySymbol(subCurrency, amountCents)}
                  {((isInputAddon && currency === user?.currency) ||
                    deposits.length === 1) && <div className="tick-icon" />}
                </>
              </li>
            ))}
          </ul>
        </ScrollBlock>

        {!noEditCurrencyBtn && isInputAddon && (
          <EditCurrenciesButton mode={mode} onClick={onClick} />
        )}
      </div>
    );
  }
);

export const EditCurrenciesButton: FC<{
  large?: boolean;
  mode?: ThemeMode;
  onClick: () => void;
}> = ({ large, mode, onClick }) => {
  const { t } = useTranslation('deposits');
  const useCommonStore = useGetCommonStore();

  const isMobileDevice = useCommonStore.use.isMobileDevice();

  return (
    <Button
      autoPaddingHorizontal
      blackTransparent={!isMobileDevice && mode !== 'dark'}
      large={large}
      onClick={onClick}
      small={!large}
      type="button"
      whiteTransparent={isMobileDevice || mode === 'dark'}
      wide
    >
      {t('editCurrency.title')}
    </Button>
  );
};
